import styled from '@emotion/styled';
import { keyframes } from '@emotion/core';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { config as translationConfig } from 'src/config/config';
import { adminService, toastNotificationsService } from 'src/config/rootService';
import { PdfDocument } from 'src/modules/admin/components/PdfDocument';
import { config } from 'src/modules/admin/config/config';
import closeIcon from 'src/public/img/icon-close-circle.svg';
import { Modal } from 'src/shared/components/Modal';
import { color, font, layout } from 'src/shared/styles/variables';

import { Icon, Menu, Spin } from 'antd';
import { ClickParam } from 'antd/lib/menu';
import {
    AvailableReportType,
    PossibleReportAndSectionType as ReportOrSection,
} from 'src/generated/webapp_gql';

const { SubMenu } = Menu;

enum LookerItemTypes {
    AvailableReport = 'AvailableReportType',
    UnavailableReport = 'UnavailableReportType',
    AvailableSection = 'AvailableSectionType',
    UnavailableSection = 'UnavailableSectionType',
}

const PageWrapper = styled.div({
    backgroundColor: color.white,
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
});

const PageContent = styled.div({
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: layout.containerWidth,
    padding: '1rem 1.5rem 0 1.5rem',
});

const PageHeader = styled.h1({
    fontFamily: 'Avenir',
    fontSize: '1.75rem',
    fontWeight: 600,
    marginBottom: 0,
});

const SubmenuItem = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
});

const SubmenuIcon = styled('i')(({ iconUrl }: { iconUrl: string }) => ({
    display: 'block',
    backgroundImage: `url('${iconUrl}')`,
    width: '16px',
    height: '16px',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    flexShrink: 1,
}));

const SubmenuText = styled.div({
    flexShrink: 1,
    maxWidth: 220,
});

const DashboardHeader = styled.section({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
});

const MainDashboardSection = styled.div({
    flex: '1 1 300px',
    position: 'relative',
    overflow: 'hidden',
});

const submenuItemSelector = '.ant-menu-submenu';
const submenuTitleSelector = '.ant-menu-submenu-title';
const selectedSubmenuTitleSelector = `.ant-menu-submenu-selected ${submenuTitleSelector}`;
const allMenuItemsSelector = '.ant-menu-item';
const selectedItemSelector = `${allMenuItemsSelector}.ant-menu-item-selected`;
const menuItemTitleSelector = '.menu-item-title';
const menuItemSubtitleSelector = 'div.menu-item-subtitle';

const menuItemHeight = 70;
const selectedTextFontWeight = 800;
const menuItemFontWeight = 500;
const menuWidth = 320;
const fadeIn = keyframes({
    '0%': {
        left: -menuWidth,
    },
    '100%': {
        left: 0,
    },
});
const fadeOut = keyframes({
    '0%': {
        left: 0,
    },
    '100%': {
        left: -menuWidth,
    },
});

const StyledMenu = styled(Menu)(({ isMenuOpen }: { isMenuOpen: boolean }) => ({
    position: 'absolute',
    zIndex: 10,
    height: '100%',
    top: 0,
    left: isMenuOpen ? 0 : -menuWidth,
    maxWidth: menuWidth,
    animation: `${isMenuOpen ? fadeIn : fadeOut} .4s ease 1`,
    [allMenuItemsSelector]: {
        color: color.ebonyClay,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
    },
    [menuItemTitleSelector]: {
        color: color.ebonyClay,
        fontSize: font.size.mediumExtended,
        fontWeight: menuItemFontWeight,
        height: `${menuItemHeight}px`,
        display: 'flex',
        alignItems: 'center',
    },
    [menuItemSubtitleSelector]: {
        fontSize: font.size.small,
        lineHeight: `${font.size.small}px`,
    },
    [submenuItemSelector]: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        [submenuTitleSelector]: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            color: color.ebonyClay,
            fontSize: font.size.mediumExtended,
            fontWeight: menuItemFontWeight,
            height: `${menuItemHeight}px`,
        },
    },
    [selectedSubmenuTitleSelector]: {
        fontWeight: selectedTextFontWeight,
    },
    [selectedItemSelector]: {
        background: color.whiteIce,
        fontWeight: selectedTextFontWeight,
        [menuItemTitleSelector]: {
            fontWeight: selectedTextFontWeight,
        },
        '&:after': {
            content: 'none',
        },
    },
}));

const LookerDashboard = styled.iframe({
    border: 0,
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: '100%',
});

const PdfModal = styled(Modal)({
    '&.ant-modal': {
        height: '90%',
    },
    '.ant-modal-content': {
        height: '90%',
        maxHeight: '90%',
        overflowY: 'scroll',
        borderRadius: '16px',
        scrollWidth: 0,
        position: 'static',
    },
    '.ant-modal-content::-webkit-scrollbar': {
        width: 0,
    },
});

const MenuFoldIcon = styled(Icon)({
    display: 'inline-block',
    fontSize: '1.5rem',
    margin: '14px 14px 14px 0',
});

const ReportPlaceHolder = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    h2: {
        marginTop: 30,
    },
});

const SpinnerWrapper = styled.div({
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});

const AntSpinnerIcon = styled(Icon)({
    fontSize: font.size.headerPlus,
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
});

export const AdminReportsPage = (): JSX.Element => {
    const { t } = useTranslation();
    const [isGlossaryModalVisible, setGlossaryModalVisible] = useState(false);
    const [reports, setReports] = useState<ReportOrSection[] | undefined>();
    const [selectedReport, setSelectedReport] = useState<ReportOrSection | undefined>();
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(true);

    const getReportsList = async (): Promise<void> => {
        let reportsList;
        try {
            reportsList = await adminService.fetchReportsInfo();
        } catch (e) {
            toastNotificationsService.renderErrorToast(t('admin:looker.general_error'));
            return;
        }

        setReports(reportsList);
        if (reportsList && reportsList.length > 0) {
            setSelectedReport(reportsList[0]);
        }
    };

    useEffect(() => {
        getReportsList();
    }, []);

    const hasChildren = (item: ReportOrSection) => 'reports' in item && item.reports?.length;

    const ReportItem = (props): JSX.Element => {
        const { item, eventKey } = props;
        if (hasChildren(item)) {
            return (
                <SubMenu key={eventKey} title={item.title} {...props}>
                    {item.reports.map(report => (
                        <Menu.Item key={report.reportOrSectionEnum} {...props}>
                            <SubmenuItem>
                                <SubmenuIcon iconUrl={report.iconUrl} />{' '}
                                <SubmenuText>{report.title}</SubmenuText>
                            </SubmenuItem>
                        </Menu.Item>
                    ))}
                </SubMenu>
            );
        }

        const MenuItemStyle = item.subtitle
            ? { height: menuItemHeight + 20 }
            : { height: menuItemHeight };
        return (
            <Menu.Item key={eventKey} style={{ height: 'auto' }} {...props}>
                <div className="menu-item-title" style={MenuItemStyle}>
                    {item.title}
                </div>
                {item.subtitle && <div className="menu-item-subtitle">{item.subtitle}</div>}
            </Menu.Item>
        );
    };

    const getReportUrl = async (report: AvailableReportType): Promise<string> => {
        const newLookerSSOLink = await adminService.fetchLookerSSO(
            report.reportOrSectionEnum || '',
        );
        return newLookerSSOLink?.url || report.lookerUrl || '';
    };

    const getFlatReportsList = (): ReportOrSection[] => {
        if (!reports) {
            return [];
        }

        return reports.flatMap(reportItem => {
            if (reportItem.__typename === LookerItemTypes.AvailableSection) {
                return reportItem.reports as ReportOrSection[];
            }
            return reportItem;
        });
    };

    const handleMenuSelection = async ({ key }: ClickParam): Promise<void> => {
        const clickedReport = getFlatReportsList().find(
            report => report.reportOrSectionEnum === key,
        );

        const shouldUpdateReport = !selectedReport || selectedReport.reportOrSectionEnum !== key;
        if (!clickedReport || !shouldUpdateReport) {
            return;
        }

        if (clickedReport.__typename === LookerItemTypes.AvailableReport) {
            clickedReport.lookerUrl = await getReportUrl(clickedReport);
        }
        setSelectedReport(clickedReport);
        setIsMenuOpen(false);
    };

    const ReportsMenu = ({
        reportsList,
        selection,
    }: {
        reportsList: ReportOrSection[];
        selection: ReportOrSection;
    }): JSX.Element => {
        const selectedKeyProps = selection
            ? {
                  defaultSelectedKeys: [selection.reportOrSectionEnum || ''],
              }
            : null;
        return (
            <StyledMenu
                onClick={handleMenuSelection}
                {...selectedKeyProps}
                mode="inline"
                isMenuOpen={isMenuOpen}
            >
                {reportsList.map((item: ReportOrSection) => {
                    return <ReportItem item={item} key={item.reportOrSectionEnum} />;
                })}
            </StyledMenu>
        );
    };

    const UnavailableReport = ({ imageUrl, title, subtitle }): JSX.Element => {
        return (
            <ReportPlaceHolder>
                <img src={imageUrl} alt="Report placeholder" width={228} />
                <h2>{title}</h2>
                <p>{subtitle}</p>
            </ReportPlaceHolder>
        );
    };

    const ReportComponent = ({
        reportsList,
        selection,
    }: {
        reportsList: ReportOrSection[] | undefined;
        selection: ReportOrSection | undefined;
    }): JSX.Element => {
        if (reportsList === undefined || selection === undefined) {
            return (
                <SpinnerWrapper>
                    <Spin
                        delay={0}
                        indicator={
                            <AntSpinnerIcon
                                type="loading"
                                spin
                                data-testid="centered-spinner"
                                style={{ color: color.royalBlue, fontSize: font.size.headerPlus }}
                            />
                        }
                    />
                </SpinnerWrapper>
            );
        }

        if ('imageUrl' in selection) {
            return (
                <UnavailableReport
                    imageUrl={selection.imageUrl}
                    title={selection.title}
                    subtitle={selection.subtitle}
                />
            );
        }

        if ('lookerUrl' in selection) {
            return <LookerDashboard src={selection.lookerUrl ?? undefined} role="main" />;
        }

        return <div>{t(translationConfig.messages.genericError)}</div>;
    };

    const memoizedReport = useMemo(() => {
        return <ReportComponent reportsList={reports} selection={selectedReport} />;
    }, [selectedReport?.reportOrSectionEnum]);

    return (
        <>
            <PageWrapper>
                <PageContent>
                    <DashboardHeader>
                        <PageHeader>
                            {reports !== undefined && (
                                <MenuFoldIcon
                                    type={isMenuOpen ? 'menu-fold' : 'menu-unfold'}
                                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                                />
                            )}
                            {t('admin:dashboard.reporting.title')}
                        </PageHeader>
                    </DashboardHeader>
                    <MainDashboardSection>
                        {reports && selectedReport && (
                            <ReportsMenu reportsList={reports} selection={selectedReport} />
                        )}
                        {memoizedReport}
                    </MainDashboardSection>
                </PageContent>
            </PageWrapper>
            <PdfModal
                visible={isGlossaryModalVisible}
                footer={null}
                width="90%"
                onCancel={() => setGlossaryModalVisible(false)}
                closeIcon={<img src={closeIcon} alt="close icon" />}
            >
                <PdfDocument file={config.glossaryPdfUrl} />
            </PdfModal>
        </>
    );
};
